import isNil from 'lodash/isNil';
import pickBy from 'lodash/pickBy';

/**
 * This should use the Data Management (Lexicon++) mechanics to do an event/property rename
 * Currently it is expected that from an app when the event definitions are fetched for the event/property dropdowns
 * They will call registerEventDisplayNames and registerPropertyDisplayNames
 * TODO: In the near future as we bundle event dropdowns into a component, the registry will initialized as part of that
 */

import {EVENTS, PROPERTIES} from './constants';

interface DisplayNameMappings {
  events: {
    [key: string]: string;
  };
  properties: {
    [key: string]: string;
  };
}

declare global {
  interface Window {
    __displayNameMappings: DisplayNameMappings;
  }
}

/**
 * @typedef {Object} DisplayNameMappings
 * @prop {[event: string]: string} events
 * @prop {[property: string]: string} properties
 *
 * @type {DisplayNameMappings}
 */
let displayNameMappings: DisplayNameMappings = {
  events: {},
  properties: {},
};

/**
 * Defensively access mp.report.globals
 */
function getMpReportGlobals() {
  // eslint-disable-next-line no-undef
  return (typeof mp === `object` && mp.report && mp.report.globals) || {};
}

/**
 * @param {string} event
 * @returns {string}
 */
export function getEventDisplayName(event: string): string {
  const eventMappings = displayNameMappings.events;
  if (eventMappings.hasOwnProperty(event) && !isNil(eventMappings[event])) {
    return eventMappings[event];
  }

  return event;
}

/**
 * @param {string} property
 * @returns {string}
 */
export function getPropertyDisplayName(propertyName: string, dataGroupId): string {
  const propertyMappings = displayNameMappings.properties;
  if (!dataGroupId && propertyMappings.hasOwnProperty(propertyName) && !isNil(propertyMappings[propertyName])) {
    return propertyMappings[propertyName];
  }

  return propertyName;
}

function filterFalseyValues(displayNames) {
  return pickBy(displayNames, (value) => Boolean(value));
}

/**
 * @param {[event: string]: string} displayNames
 */
export function registerEventDisplayNames(displayNames: {[event: string]: string} = {}) {
  const displayNameOverrides = filterFalseyValues(displayNames);
  const mpGlobalMappedEvents = filterFalseyValues(getMpReportGlobals().mapped_events || {});
  displayNameMappings.events = {...EVENTS, ...mpGlobalMappedEvents, ...displayNameOverrides};
}

/**
 * @param {[property: string]: string} displayNames
 */
export function registerPropertyDisplayNames(displayNames: {[property: string]: string} = {}) {
  const mpGlobalMappedProperties = filterFalseyValues(getMpReportGlobals().mapped_properties || {});
  displayNameMappings.properties = {...PROPERTIES, ...mpGlobalMappedProperties, ...displayNames};
}

export function registerDefaultDisplayNames() {
  if (typeof window === `object`) {
    // We store this in a window global since mp-common utils can be part of many (~10) different .js files in a report page
    // and we want a singleton store of display name mappings everywhere
    displayNameMappings = window.__displayNameMappings = window.__displayNameMappings || displayNameMappings;
  }

  registerEventDisplayNames();
  registerPropertyDisplayNames();
}

// Register initial names from constants;
registerDefaultDisplayNames();
